/**
 * Phase 23 — Jobs page.
 *
 * Flat, filterable list view of all bookings. Complements the visual Calendar
 * with a tabular layout that's easier for desk-work / bulk scanning.
 *
 * Row click deep-links into /calendar?bookingId=<id> which re-opens the SAME
 * BookingDetail sheet the Calendar already renders — no duplicated panel code.
 *
 * Filter / sort / pagination are all server-driven via GET /api/bookings and
 * GET /api/bookings/summary (Phase 23 endpoint extensions). Audit-mode
 * exclusion is handled server-side untouched — this page does not add or
 * remove any cash-hiding logic.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { linkifyText } from "@/lib/linkify";
import {
  startOfIsoWeek, endOfIsoWeek, isoWeekOf, formatIsoWeekKey, parseIsoWeekKey,
  dateOfIsoWeek, isoWeeksInYearList, toLocalYMD,
} from "@/lib/isoWeek";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Search, Filter, X, ChevronUp, ChevronDown, RefreshCcw, ChevronLeft, ChevronRight,
  CalendarDays, Users, Receipt, AlertCircle, Wallet, CheckCircle2, Loader2, MailX,
} from "lucide-react";
import { toast } from "sonner";
import { PageRefreshButton } from "@/components/PageRefreshButton";
import { DetailsPendingDot } from "@/components/DetailsPendingBadge";
import PaymentBadge from "@/components/PaymentBadge";
import JobPreviewDialog from "@/components/JobPreviewDialog";
import { formatBookingStamp } from "@/lib/dates";
import { usePersistedFilters, clearPersistedFilters } from "@/lib/usePersistedFilters";

// --- date presets ----------------------------------------------------------
function ymd(d) { return d.toISOString().slice(0, 10); }
function startOfToday() { const d = new Date(); d.setHours(0,0,0,0); return d; }
function startOfWeek() {
  const d = startOfToday();
  // Mon-based week start
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d;
}
function endOfWeek() { const d = startOfWeek(); d.setDate(d.getDate() + 6); return d; }
function startOfMonth() { const d = startOfToday(); d.setDate(1); return d; }
function endOfMonth() {
  const d = startOfMonth();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
}
function startOfLastMonth() {
  const d = startOfMonth();
  d.setMonth(d.getMonth() - 1);
  return d;
}
function endOfLastMonth() {
  const d = startOfMonth();
  d.setDate(0);
  return d;
}

const DATE_PRESETS = [
  { id: "today",      label: "Today",      range: () => [startOfToday(), startOfToday()] },
  { id: "this_week",  label: "This week",  range: () => [startOfWeek(), endOfWeek()] },
  { id: "this_month", label: "This month", range: () => [startOfMonth(), endOfMonth()] },
  { id: "last_month", label: "Last month", range: () => [startOfLastMonth(), endOfLastMonth()] },
  { id: "custom",     label: "Custom",     range: null },
];

// Phase M — paid/unpaid row tinting. Closed states (write-off, cancel,
// void) stay neutral so the manager isn't visually alarmed by rows that
// aren't collection problems. Everything else without a `paid` marker
// tints red so unpaid jobs jump out at a glance.
// Phase P.3 Item 4 — amber "not sent" tint overrides paid/unpaid when the
// booking has a linked invoice that's been issued but not emailed yet.
// Bookings without a linked invoice (Simple Cash / off-books) keep the
// Phase M paid/unpaid rules.
const PAYMENT_TINT_CLOSED = new Set(["write_off", "unable_to_collect", "cancelled", "voided"]);
function paymentTintBucket(status) {
  const s = (status || "").toLowerCase();
  if (s === "paid") return "paid";
  if (PAYMENT_TINT_CLOSED.has(s)) return "closed";
  return "unpaid";
}
function invoiceSendBucketForBooking(b) {
  // Only meaningful when a linked invoice exists.
  const invStatus = (b?.linked_invoice_status || "").toLowerCase();
  if (!invStatus) return null;
  if (["voided", "cancelled", "cancelled"].includes(invStatus)) return null;
  const sentAt = b?.linked_invoice_sent_at;
  if (invStatus === "draft") return "not_sent";
  if (!sentAt) return "not_sent";
  return "sent";
}
function paymentRowTint(booking) {
  // Support the old signature (status string) for legacy call-sites still
  // passing only a status. Migrate to booking-object form for full logic.
  const status = typeof booking === "string" ? booking : booking?.payment_status;
  const sendBucket = typeof booking === "object" ? invoiceSendBucketForBooking(booking) : null;
  // Amber precedence: an issued-but-not-sent invoice takes over any other tint.
  if (sendBucket === "not_sent") return "bg-amber-500/10 hover:bg-amber-500/15";
  const bucket = paymentTintBucket(status);
  if (bucket === "paid")   return "bg-emerald-500/10 hover:bg-emerald-500/15";
  if (bucket === "closed") return "bg-slate-500/5 hover:bg-slate-500/10";
  return "bg-red-500/10 hover:bg-red-500/15";
}

const PAYMENT_STATUSES = [
  { v: "awaiting_payment",    label: "Awaiting payment" },
  { v: "paid",                label: "Paid" },
  { v: "overdue",             label: "Overdue" },
  { v: "write_off",           label: "Write-off" },
  { v: "unable_to_collect",   label: "Unable to collect" },
];

const PAYMENT_TYPES = [
  { v: "simple_cash",        label: "Simple Cash" },
  { v: "cash_with_invoice",  label: "Cash + Invoice" },
  { v: "vat",                label: "VAT" },
  { v: "rct",                label: "RCT" },
];

const PAGE_SIZE = 50;

// --- summary chip ----------------------------------------------------------
function SummaryChip({ icon: Icon, label, value, tone = "default", testId }) {
  const toneCls = {
    default: "bg-secondary text-foreground border-border",
    accent:  "bg-amber-50 text-amber-900 border-amber-300",
    warn:    "bg-rose-50 text-rose-900 border-rose-300",
  }[tone];
  return (
    <div
      className={`flex items-center gap-2 rounded-[6px] border px-3 py-1.5 ${toneCls}`}
      data-testid={testId}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
      <span className="mono font-extrabold tabular-nums">{value}</span>
    </div>
  );
}

// --- multi-checkbox dropdown ----------------------------------------------
function MultiPicker({ label, options, value, onChange, testId, placeholder = "Any" }) {
  const selectedLabels = options.filter((o) => value.includes(o.v)).map((o) => o.label);
  const summary = selectedLabels.length === 0
    ? placeholder
    : selectedLabels.length <= 2
      ? selectedLabels.join(", ")
      : `${selectedLabels.length} selected`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-9 w-full justify-between font-normal text-left"
          data-testid={testId}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2 w-56" align="start">
        <div className="flex items-center justify-between mb-1 px-1">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</span>
          {value.length > 0 && (
            <button
              type="button"
              className="text-[11px] text-accent hover:underline"
              onClick={() => onChange([])}
              data-testid={`${testId}-clear`}
            >
              Clear
            </button>
          )}
        </div>
        <div className="max-h-60 overflow-y-auto space-y-0.5">
          {options.map((opt) => {
            const checked = value.includes(opt.v);
            return (
              <label
                key={opt.v}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary cursor-pointer text-sm"
                data-testid={`${testId}-opt-${opt.v}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...value, opt.v]);
                    else onChange(value.filter((v) => v !== opt.v));
                  }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- customer picker (search + select) -------------------------------------
function CustomerPicker({ customers, value, onChange, testId }) {
  const [q, setQ] = useState("");
  const matches = useMemo(() => {
    if (!q.trim()) return customers.slice(0, 50);
    const needle = q.toLowerCase();
    return customers
      .filter((c) =>
        (c.company_name || "").toLowerCase().includes(needle) ||
        (c.contact_name || "").toLowerCase().includes(needle) ||
        (c.phones || []).some((p) => (p || "").toLowerCase().includes(needle))
      )
      .slice(0, 50);
  }, [q, customers]);
  const selected = customers.find((c) => c.id === value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-9 w-full justify-between font-normal text-left"
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.company_name : "Any customer"}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2 w-72" align="start">
        <Input
          placeholder="Search customers…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8 mb-2"
          autoFocus
          data-testid={`${testId}-search`}
        />
        <div className="max-h-72 overflow-y-auto">
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="w-full text-left px-2 py-1.5 text-sm text-rose-700 hover:bg-rose-50 rounded"
              data-testid={`${testId}-clear`}
            >
              Clear selection
            </button>
          )}
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange(c.id)}
              className={`w-full text-left px-2 py-1.5 text-sm hover:bg-secondary rounded ${value === c.id ? "bg-secondary font-semibold" : ""}`}
              data-testid={`${testId}-opt-${c.id}`}
            >
              <div className="truncate">{c.company_name || c.contact_name}</div>
              {c.contact_name && c.company_name && (
                <div className="text-[11px] text-muted-foreground truncate">{c.contact_name}</div>
              )}
            </button>
          ))}
          {matches.length === 0 && (
            <div className="px-2 py-3 text-[11px] text-muted-foreground text-center">No matches</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- sortable column header ------------------------------------------------
// Phase 37 follow-up — EU-style 3-line date cell for Jobs table.
// Returns { weekday: "Monday", date: "30/05/26", time: "08:00" }.
function _euDateParts(iso) {
  if (!iso) return { weekday: "", date: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { weekday: "", date: "—", time: "" };
  return {
    weekday: d.toLocaleDateString("en-GB", { weekday: "long" }),
    date:    d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }),
    time:    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }),
  };
}

// Short, single-word payment-status labels so the pill never wraps.
const _PAYMENT_STATUS_SHORT = {
  awaiting_payment: "Awaiting",
  paid: "Paid",
  overdue: "Overdue",
  pending_manager_approval: "Pending",
  write_off: "Write-off",
  unable_to_collect: "No collect",
};
function _shortPaymentStatus(code) {
  if (!code) return "—";
  return _PAYMENT_STATUS_SHORT[code] || code.replace(/_/g, " ");
}

function SortableTh({ children, sortKey, currentSort, currentDir, onSort, testId, className = "" }) {
  const active = currentSort === sortKey;
  const dir = active ? currentDir : null;
  return (
    <th
      className={`text-left px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground cursor-pointer select-none ${className}`}
      onClick={() => onSort(sortKey)}
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (dir === "asc"
          ? <ChevronUp className="h-3 w-3" />
          : <ChevronDown className="h-3 w-3" />
        )}
      </span>
    </th>
  );
}

// --- main page -------------------------------------------------------------
export default function Jobs() {
  const navigate = useNavigate();

  // Filters
  const [datePreset, setDatePreset] = useState("this_week");
  // Phase G — ISO-week selection (`YYYY-WNN`). Default = current week.
  // Persists in `?week=` URL query so reload keeps the selection.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialWeekKey = (() => {
    const fromQ = parseIsoWeekKey(searchParams.get("week") || "");
    if (fromQ) return formatIsoWeekKey(fromQ.year, fromQ.week);
    const now = isoWeekOf(new Date());
    return formatIsoWeekKey(now.year, now.week);
  })();
  const [weekKey, setWeekKey] = useState(initialWeekKey);
  // Phase P.9 — "All weeks" toggle removes the date range constraint so
  // search + other filters apply across the full dataset. URL `?week=all`
  // persists the state. Default = current week (matches pre-P.9 UX).
  const [allWeeks, setAllWeeks] = useState(() => searchParams.get("week") === "all");
  const weekRange = useMemo(() => {
    const p = parseIsoWeekKey(weekKey) || isoWeekOf(new Date());
    const start = dateOfIsoWeek(p.year, p.week);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { year: p.year, week: p.week, start, end };
  }, [weekKey]);
  const [from, setFrom] = useState(allWeeks ? "" : toLocalYMD(weekRange.start));
  const [to, setTo] = useState(allWeeks ? "" : toLocalYMD(weekRange.end));
  // Phase G / P.9 — keep from/to + URL in sync with the selected ISO
  // week OR the "All weeks" toggle.
  useEffect(() => {
    if (allWeeks) {
      setFrom("");
      setTo("");
    } else {
      setFrom(toLocalYMD(weekRange.start));
      setTo(toLocalYMD(weekRange.end));
    }
    const next = new URLSearchParams(searchParams);
    next.set("week", allWeeks ? "all" : weekKey);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey, allWeeks]);
  const [customerId, setCustomerId] = useState(null);
  const [staffIds, setStaffIds] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  // Phase P.11 — filter chip: show only jobs whose linked invoice failed
  // to send because the customer has no email on file.
  const [sendBlockedOnly, setSendBlockedOnly] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Phase P.32 — one-shot hydration of persisted filters (24h TTL).
  const filterBootRef = useRef(false);
  useEffect(() => {
    if (filterBootRef.current) return;
    filterBootRef.current = true;
    try {
      const raw = localStorage.getItem("jbk:jobs:filters");
      if (!raw) return;
      const blob = JSON.parse(raw);
      if (!blob || !blob.savedAt || Date.now() - Number(blob.savedAt) > 24 * 60 * 60 * 1000) {
        localStorage.removeItem("jbk:jobs:filters");
        return;
      }
      const f = blob.filters || {};
      if (f.customerId !== undefined) setCustomerId(f.customerId);
      if (Array.isArray(f.staffIds)) setStaffIds(f.staffIds);
      if (Array.isArray(f.paymentStatuses)) setPaymentStatuses(f.paymentStatuses);
      if (Array.isArray(f.paymentTypes)) setPaymentTypes(f.paymentTypes);
      if (typeof f.sendBlockedOnly === "boolean") setSendBlockedOnly(f.sendBlockedOnly);
      if (typeof f.searchText === "string") setSearchText(f.searchText);
    } catch { /* corrupt blob — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Persist on every filter change.
  useEffect(() => {
    if (!filterBootRef.current) return;
    try {
      localStorage.setItem("jbk:jobs:filters", JSON.stringify({
        savedAt: Date.now(),
        filters: { customerId, staffIds, paymentStatuses, paymentTypes, sendBlockedOnly, searchText },
      }));
    } catch { /* quota — ignore */ }
  }, [customerId, staffIds, paymentStatuses, paymentTypes, sendBlockedOnly, searchText]);

  // Sort + pagination
  const [sortBy, setSortBy] = useState("scheduled_start");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  // Data
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ count: 0, total_amount: 0, in_accounts_total: 0, awaiting_payment: 0, overdue: 0 });
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [currency, setCurrency] = useState("EUR");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // Phase P.3 Item 3 — bump this to force the fetch effect below to re-run.
  const [refreshTick, setRefreshTick] = useState(0);
  // Phase 63.3 — Mark Paid modal state + tenant lookups.
  const [tenantPaymentTypes, setTenantPaymentTypes] = useState([]);
  const [paidMethods, setPaidMethods] = useState([]);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  // Phase P.32 — preview popup on row click.
  const [previewBooking, setPreviewBooking] = useState(null);

  // Debounce free-text search so we don't fire on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [searchText]);

  // Bootstrap: customers + staff + settings (currency).
  useEffect(() => {
    (async () => {
      try {
        const [cRes, sRes, settingsRes, ptRes, pmRes] = await Promise.all([
          api.get("/customers"),
          api.get("/staff"),
          api.get("/settings"),
          api.get("/settings/payment-types").catch(() => ({ data: [] })),
          api.get("/settings/paid-methods").catch(() => ({ data: [] })),
        ]);
        setCustomers(cRes.data || []);
        setStaff((sRes.data || []).filter((s) => s.active !== false));
        setCurrency(settingsRes.data?.currency || "EUR");
        setTenantPaymentTypes((ptRes.data || []).filter((p) => !p.archived));
        setPaidMethods((pmRes.data || []).filter((p) => !p.archived));
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
      }
    })();
  }, []);

  // Build the query params shared by /bookings and /bookings/summary.
  const buildParams = () => {
    const p = { page, page_size: PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir };
    if (from) p.from = from;
    if (to) p.to = to;
    if (customerId) p.customer_id = customerId;
    if (staffIds.length > 0) p.staff_ids = staffIds.join(",");
    if (paymentStatuses.length > 0) p.payment_status = paymentStatuses.join(",");
    if (paymentTypes.length > 0) p.payment_type = paymentTypes.join(",");
    if (debouncedSearch) p.q = debouncedSearch;
    return p;
  };

  // Fetch list + summary in parallel whenever filters/sort/page change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const params = buildParams();
      // Summary uses the same params minus pagination/sort
      const sumParams = { ...params };
      delete sumParams.page; delete sumParams.page_size;
      delete sumParams.sort_by; delete sumParams.sort_dir;
      try {
        const [listRes, sumRes] = await Promise.all([
          api.get("/bookings", { params }),
          api.get("/bookings/summary", { params: sumParams }),
        ]);
        if (cancelled) return;
        setBookings(listRes.data || []);
        setSummary(sumRes.data || {});
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e?.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, customerId, staffIds.join(","), paymentStatuses.join(","), paymentTypes.join(","), debouncedSearch, sortBy, sortDir, page, refreshTick]);

  // When non-page filters change, reset to page 1.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [from, to, customerId, staffIds.join(","), paymentStatuses.join(","), paymentTypes.join(","), debouncedSearch, sortBy, sortDir]);

  const applyPreset = (id) => {
    setDatePreset(id);
    const p = DATE_PRESETS.find((x) => x.id === id);
    if (p && p.range) {
      const [a, b] = p.range();
      setFrom(ymd(a)); setTo(ymd(b));
    }
  };

  const clearAll = () => {
    setDatePreset("this_month");
    const [a, b] = DATE_PRESETS[2].range();
    setFrom(ymd(a)); setTo(ymd(b));
    setCustomerId(null);
    setStaffIds([]);
    setPaymentStatuses([]);
    setPaymentTypes([]);
    setSendBlockedOnly(false);
    setSearchText("");
    // Phase P.32 — also wipe the persisted snapshot so next visit is clean.
    try { clearPersistedFilters("jobs"); } catch { /* ignore */ }
  };

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDir(key === "scheduled_start" ? "desc" : "asc");
    }
  };

  const openBooking = (id) => {
    // Phase P.32 — popup preview first, then user opts into the full page.
    const b = bookings.find((x) => x.id === id);
    if (b) setPreviewBooking(b);
  };

  // Phase 63.3 — eligibility for the Mark Paid button. Hide it when the
  // booking is already paid, cancelled, or in a terminal write-off-style
  // status. We keep it visible for ANY non-paid booking (scheduled +
  // in_progress + completed) so the manager can mark it paid the moment
  // they have the money.
  const canMarkPaid = (b) => {
    const ps = (b.payment_status || "").toLowerCase();
    const st = (b.status || "").toLowerCase();
    if (ps === "paid" || ps === "write_off" || ps === "unable_to_collect") return false;
    if (st === "cancelled") return false;
    return true;
  };

  const onMarkPaidSubmitted = (updated) => {
    if (!updated) return;
    setBookings((arr) => arr.map((x) => x.id === updated.id ? { ...x, ...updated } : x));
    // Refresh the summary chips so awaiting/overdue counters stay accurate.
    (async () => {
      try {
        const params = buildParams();
        const sumParams = { ...params };
        delete sumParams.page; delete sumParams.page_size;
        delete sumParams.sort_by; delete sumParams.sort_dir;
        const { data } = await api.get("/bookings/summary", { params: sumParams });
        setSummary(data || {});
      } catch (_e) { /* ignore */ }
    })();
  };

  const staffOptions = staff.map((s) => ({ v: s.id, label: s.name }));
  const totalPages = Math.max(1, Math.ceil((summary.count || 0) / PAGE_SIZE));
  // Phase P.11 — apply the "Invoice send blocked" filter client-side.
  // Backend augments each BookingOut with `linked_invoice_send_blocked_reason`
  // (see /app/backend/server.py:_hydrate_booking) so this is a cheap check.
  const visibleBookings = sendBlockedOnly
    ? bookings.filter((b) => (b.linked_invoice_send_blocked_reason || "") === "no_customer_email")
    : bookings;

  // Phase J — compact chip-style filter row: single horizontal flex-wrap with
  // smaller targets, inline labels, "Showing N of M" line + Clear-all link.
  // Each control's popover behaviour comes from the existing MultiPicker /
  // Select / IsoWeekPicker components — no new popover wiring needed.
  const activeFilterCount =
    (customerId ? 1 : 0)
    + (staffIds.length > 0 ? 1 : 0)
    + (paymentStatuses.length > 0 ? 1 : 0)
    + (paymentTypes.length > 0 ? 1 : 0)
    + (sendBlockedOnly ? 1 : 0)
    + (searchText.trim() ? 1 : 0);
  const filterBar = (
    <div className="space-y-2" data-testid="jobs-compact-filters">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5" data-testid="filter-chip-week">
          <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Week</span>
          {allWeeks ? (
            <button
              type="button"
              onClick={() => setAllWeeks(false)}
              className="h-8 px-3 rounded-md border border-border bg-secondary/40 hover:bg-secondary text-xs font-bold inline-flex items-center gap-1"
              data-testid="jobs-week-all-active"
              title="Currently showing every week. Click to jump back to a specific week."
            >
              All weeks
            </button>
          ) : (
            <IsoWeekPicker value={weekKey} onChange={(k) => { setAllWeeks(false); setWeekKey(k); }} />
          )}
          <button
            type="button"
            onClick={() => setAllWeeks((v) => !v)}
            className="h-8 px-2 rounded-md border border-border text-[11px] font-bold text-muted-foreground hover:bg-secondary"
            data-testid="jobs-week-toggle-all"
            title={allWeeks ? "Return to the current week" : "Remove week filter — show all jobs across all dates"}
          >
            {allWeeks ? "Pick week" : "All weeks"}
          </button>
        </div>
        <div className="h-5 w-px bg-border" aria-hidden />
        <div className="flex items-center gap-1.5 min-w-[160px]" data-testid="filter-chip-customer">
          <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Cust</span>
          <CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} testId="jobs-filter-customer" />
        </div>
        <div className="flex items-center gap-1.5 min-w-[140px]" data-testid="filter-chip-staff">
          <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Staff</span>
          <MultiPicker label="Staff" options={staffOptions} value={staffIds} onChange={setStaffIds} testId="jobs-filter-staff" placeholder="Any" />
        </div>
        <div className="flex items-center gap-1.5 min-w-[140px]" data-testid="filter-chip-pstatus">
          <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Status</span>
          <MultiPicker label="Payment status" options={PAYMENT_STATUSES} value={paymentStatuses} onChange={setPaymentStatuses} testId="jobs-filter-pstatus" placeholder="Any" />
        </div>
        <div className="flex items-center gap-1.5 min-w-[140px]" data-testid="filter-chip-ptype">
          <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Type</span>
          <MultiPicker label="Payment type" options={PAYMENT_TYPES} value={paymentTypes} onChange={setPaymentTypes} testId="jobs-filter-ptype" placeholder="Any" />
        </div>
        {/* Phase P.11 — quick chip to surface every booking whose invoice
            was left unsent because the customer has no email on file. */}
        <button
          type="button"
          onClick={() => setSendBlockedOnly((v) => !v)}
          className={`h-8 px-3 rounded-md border text-[11px] font-bold inline-flex items-center gap-1 transition-colors ${
            sendBlockedOnly
              ? "bg-rose-100 border-rose-300 text-rose-900 hover:bg-rose-200"
              : "border-border text-muted-foreground hover:bg-secondary"
          }`}
          data-testid="filter-chip-send-blocked"
          data-active={sendBlockedOnly ? "true" : "false"}
          title="Show only jobs whose invoice couldn't be sent because the customer has no email on file"
        >
          <MailX className="h-3 w-3" /> Invoice send blocked
        </button>
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]" data-testid="filter-chip-search">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Input
            placeholder="Search code · customer · address"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="h-8 text-xs"
            data-testid="jobs-filter-search"
          />
        </div>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="filter-clear-all"
          >
            <X className="h-3 w-3" /> Clear all
          </button>
        )}
      </div>
      <div
        className="text-[10px] mono uppercase tracking-wider text-muted-foreground"
        data-testid="jobs-filter-showing"
      >
        Showing {bookings.length} of {summary.count || 0} job{(summary.count || 0) === 1 ? "" : "s"}
        {activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} active` : ""}
      </div>
    </div>
  );

  return (
    <div className="page" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// JOBS</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold">All bookings</h1>
          <div className="text-xs text-muted-foreground mt-0.5">
            Flat list view — filter, sort, jump to detail.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMobileFilters((s) => !s)}
            className="md:hidden"
            data-testid="jobs-toggle-mobile-filters"
          >
            <Filter className="h-3.5 w-3.5 mr-1" /> Filters
          </Button>
          <PageRefreshButton
            onRefresh={async () => { setRefreshTick((t) => t + 1); await new Promise((r) => setTimeout(r, 400)); }}
            testId="page-refresh-btn"
            showLabel
          />
        </div>
      </div>

      {/* Sticky filter bar */}
      <div className={`sticky top-0 z-10 bg-background pb-3 -mx-2 px-2 ${showMobileFilters ? "" : "hidden"} md:block`} data-testid="jobs-filter-bar">
        <div className="bg-card border border-border rounded-[8px] p-3 sm:p-4">
          {filterBar}
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="jobs-summary">
        <SummaryChip icon={CalendarDays} label="Jobs" value={summary.count}                 testId="jobs-summary-count" />
        <SummaryChip icon={Wallet}       label="Total" value={formatCurrency(summary.total_amount, currency)} testId="jobs-summary-total" />
        <SummaryChip icon={Receipt}      label="In accounts" value={formatCurrency(summary.in_accounts_total, currency)} testId="jobs-summary-in-accounts" />
        <SummaryChip icon={AlertCircle}  label="Awaiting"   value={summary.awaiting_payment} tone="accent" testId="jobs-summary-awaiting" />
        <SummaryChip icon={AlertCircle}  label="Overdue"    value={summary.overdue} tone="warn" testId="jobs-summary-overdue" />
      </div>

      {/* Table (desktop) — Phase 37 follow-up: tightened to fit at 1280px */}
      <div className="bg-card border border-border rounded-[8px] overflow-x-auto hidden md:block" data-testid="jobs-table-wrapper">
        <table className="w-full text-xs" data-testid="jobs-table">
          <thead className="bg-secondary/50 border-b border-border sticky top-0 z-[5]">
            <tr>
              <SortableTh sortKey="booking_code"    currentSort={sortBy} currentDir={sortDir} onSort={onSort} testId="jobs-th-code">Code</SortableTh>
              <SortableTh sortKey="scheduled_start" currentSort={sortBy} currentDir={sortDir} onSort={onSort} testId="jobs-th-date">Date / Time</SortableTh>
              <SortableTh sortKey="customer_name"   currentSort={sortBy} currentDir={sortDir} onSort={onSort} testId="jobs-th-customer">Customer</SortableTh>
              <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Address</th>
              <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Staff</th>
              <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground" data-testid="jobs-th-travel">Travel</th>
              <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Template</th>
              <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Payment</th>
              <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Status</th>
              <SortableTh sortKey="subtotal"        currentSort={sortBy} currentDir={sortDir} onSort={onSort} testId="jobs-th-total" className="text-right">Total</SortableTh>
              <th className="text-right px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground w-[110px]" data-testid="jobs-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && bookings.length === 0 && (
              <tr><td colSpan={11} className="px-2 py-6 text-center text-sm text-muted-foreground" data-testid="jobs-loading">Loading…</td></tr>
            )}
            {!loading && bookings.length === 0 && (
              <tr><td colSpan={11} className="px-2 py-8 text-center text-sm text-muted-foreground" data-testid="jobs-empty">
                No bookings match these filters.
              </td></tr>
            )}
            {visibleBookings.map((b) => {
              const dt = _euDateParts(b.scheduled_start);
              return (
              <tr
                key={b.id}
                className={`border-t border-border cursor-pointer transition-colors ${paymentRowTint(b)}`}
                onClick={() => openBooking(b.id)}
                data-testid={`jobs-row-${b.id}`}
                data-payment-status={paymentTintBucket(b.payment_status)}
                data-send-status={invoiceSendBucketForBooking(b) || ""}
              >
                <td className="px-2 py-2 mono text-[11px] font-bold whitespace-nowrap">{b.booking_code}</td>
                <td className="px-2 py-2 whitespace-nowrap" data-testid={`jobs-row-${b.id}-datecell`}>
                  <div className="text-[11px] font-semibold leading-tight" data-testid={`jobs-row-${b.id}-weekday`}>{dt.weekday}</div>
                  <div className="mono tabular-nums text-[12px] leading-tight" data-testid={`jobs-row-${b.id}-date`}>{dt.date}</div>
                  <div className="mono tabular-nums text-[11px] text-muted-foreground leading-tight" data-testid={`jobs-row-${b.id}-time`}>{dt.time}</div>
                </td>
                <td className="px-2 py-2 max-w-[180px]">
                  <div className="truncate font-semibold flex items-center gap-1.5">
                    {b.linked_customer_details_pending && <DetailsPendingDot />}
                    <span className="truncate">{b.customer_name}</span>
                    {b.linked_invoice_send_blocked_reason === "no_customer_email" && (
                      <span
                        className="inline-flex items-center gap-0.5 text-rose-700"
                        title="Invoice not sent — no customer email on file"
                        data-testid={`jobs-row-${b.id}-send-blocked-icon`}
                      >
                        <MailX className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                  {b.created_at && (
                    <div
                      className="text-[10px] text-muted-foreground mono tabular-nums leading-tight mt-0.5"
                      data-testid={`jobs-row-${b.id}-created-at`}
                      title={new Date(b.created_at).toLocaleString()}
                    >
                      Booked {formatBookingStamp(b.created_at)}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 max-w-[200px]">
                  <div
                    className="truncate text-[11px] text-muted-foreground select-text"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`jobs-row-${b.id}-address`}
                  >
                    {linkifyText([b.location?.address_line, b.location?.postcode].filter(Boolean).join(", ") || "—")}
                  </div>
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-[11px]">{b.staff_name || "—"}</td>
                <td className="px-2 py-2 whitespace-nowrap" data-testid={`jobs-row-${b.id}-travel`}>
                  {b.travel_from_previous ? (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-sky-700 whitespace-nowrap"
                      title={`${b.travel_from_previous.distance_km} km · ${b.travel_from_previous.duration_min} min from the previous job on this staff member's day`}
                    >
                      ↗ {Math.round(b.travel_from_previous.duration_min)}m · {b.travel_from_previous.distance_km}km
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-[11px]">—</span>
                  )}
                </td>
                <td className="px-2 py-2 max-w-[140px]"><div className="truncate text-[11px]">{b.template_snapshot?.name || "—"}</div></td>
                <td className="px-2 py-2"><PaymentBadge job={b} size="sm" /></td>
                <td className="px-2 py-2">
                  <Badge variant="outline" className="text-[10px] whitespace-nowrap" data-testid={`jobs-row-${b.id}-pstatus`}>
                    {_shortPaymentStatus(b.payment_status)}
                  </Badge>
                </td>
                <td className="px-2 py-2 text-right mono tabular-nums font-bold whitespace-nowrap" title="Total incl. VAT" data-testid={`jobs-row-${b.id}-total`}>
                  {formatCurrency(b.total_incl_vat != null ? b.total_incl_vat : (b.subtotal || 0), currency)}
                  <span className="block text-[9px] mono uppercase tracking-widest text-muted-foreground/80 font-normal">incl. VAT</span>
                </td>
                <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()} data-testid={`jobs-row-${b.id}-actions`}>
                  {canMarkPaid(b) ? (
                    <MarkPaidQuickPopover
                      booking={b}
                      paidMethods={paidMethods}
                      paymentTypes={tenantPaymentTypes}
                      currency={currency}
                      onDone={onMarkPaidSubmitted}
                      testIdPrefix={`jobs-row-${b.id}`}
                      variant="row"
                    />
                  ) : (
                    <span className="text-[10px] text-muted-foreground mono uppercase">—</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Stacked cards (mobile) */}
      <div className="space-y-2 md:hidden" data-testid="jobs-cards">
        {loading && bookings.length === 0 && (
          <div className="text-center py-6 text-sm text-muted-foreground">Loading…</div>
        )}
        {!loading && bookings.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground bg-card border border-border rounded-[8px]" data-testid="jobs-empty-mobile">
            No bookings match these filters.
          </div>
        )}
        {visibleBookings.map((b) => (
          <div
            key={b.id}
            className={`border rounded-[8px] p-3 transition-colors ${paymentRowTint(b)} border-border hover:border-accent`}
            data-testid={`jobs-card-${b.id}`}
            data-payment-status={paymentTintBucket(b.payment_status)}
            data-send-status={invoiceSendBucketForBooking(b) || ""}
          >
            <button
              type="button"
              onClick={() => openBooking(b.id)}
              className="w-full text-left"
              data-testid={`jobs-card-open-${b.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{b.booking_code}</span>
                <PaymentBadge job={b} size="sm" />
              </div>
              <div className="mt-1 font-extrabold flex items-center gap-1.5">
                {b.linked_customer_details_pending && <DetailsPendingDot />}
                <span>{b.customer_name}</span>
              </div>
              <div className="text-[12px] text-muted-foreground mt-0.5">
                <span className="mono tabular-nums">{new Date(b.scheduled_start).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
                {b.staff_name && <span> · {b.staff_name}</span>}
              </div>
              {b.created_at && (
                <div
                  className="text-[10px] text-muted-foreground mt-0.5 mono tabular-nums"
                  data-testid={`jobs-card-${b.id}-created-at`}
                >
                  Booked {formatBookingStamp(b.created_at)}
                </div>
              )}
              {(b.location?.address_line || b.location?.postcode) && (
                <div
                  className="text-[11px] text-muted-foreground mt-0.5 truncate select-text"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`jobs-card-${b.id}-address`}
                >
                  {linkifyText([b.location?.address_line, b.location?.postcode].filter(Boolean).join(", "))}
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <Badge variant="outline" className="text-[10px]">{(b.payment_status || "—").replace(/_/g, " ")}</Badge>
                <span className="mono tabular-nums font-bold" title="Total incl. VAT">
                  {formatCurrency(b.total_incl_vat != null ? b.total_incl_vat : (b.subtotal || 0), currency)}
                  <span className="ml-1 text-[9px] uppercase tracking-widest text-muted-foreground font-normal">incl. VAT</span>
                </span>
              </div>
            </button>
            {canMarkPaid(b) && (
              <div className="mt-2">
                <MarkPaidQuickPopover
                  booking={b}
                  paidMethods={paidMethods}
                  paymentTypes={tenantPaymentTypes}
                  currency={currency}
                  onDone={onMarkPaidSubmitted}
                  testIdPrefix={`jobs-row-${b.id}`}
                  variant="card"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Phase P.32 — quick preview popup on row click */}
      <JobPreviewDialog
        booking={previewBooking}
        currency={currency}
        open={!!previewBooking}
        onOpenChange={(o) => { if (!o) setPreviewBooking(null); }}
      />

      {/* Phase 63.3 — Mark Paid modal */}
      {markPaidTarget && (
        <MarkPaidDialog
          booking={markPaidTarget}
          paymentTypes={tenantPaymentTypes}
          paidMethods={paidMethods}
          currency={currency}
          onClose={() => setMarkPaidTarget(null)}
          onDone={(updated) => {
            onMarkPaidSubmitted(updated);
            setMarkPaidTarget(null);
          }}
        />
      )}

      {/* Pager */}
      <div className="flex items-center justify-between mt-4 text-sm" data-testid="jobs-pager">
        <div className="text-muted-foreground">
          Page <span className="mono font-bold">{page}</span> of <span className="mono font-bold">{totalPages}</span> · <span className="mono">{summary.count}</span> total
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="jobs-pager-prev">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="jobs-pager-next">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}


// Phase G — Compact ISO-week picker. Button shows the current selection;
// the popover lists every ISO week of the year with prev/next year nav.
function IsoWeekPicker({ value, onChange }) {
  const parsed = parseIsoWeekKey(value) || isoWeekOf(new Date());
  const [popoverYear, setPopoverYear] = useState(parsed.year);
  const [open, setOpen] = useState(false);
  // Phase N — scroll the selected week into view when the popover opens.
  // Pre-fix, the list opened scrolled at week 1 forcing the manager to
  // scroll ~26 rows to reach today. `useLayoutEffect` + a small
  // requestAnimationFrame double-tap keeps the scroll consistent across
  // Radix's own portal mount timing.
  const selectedRowRef = useRef(null);
  const scrollWhenReady = useCallback(() => {
    const node = selectedRowRef.current;
    if (!node) return;
    // `block: "center"` matches the brief — manager sees today with
    // context above and below rather than pinned to the top edge.
    try { node.scrollIntoView({ block: "center", behavior: "auto" }); } catch { /* jsdom */ }
  }, []);
  useEffect(() => {
    if (!open) return;
    // First frame: the list has just mounted but layout may not be
    // committed. requestAnimationFrame guarantees the DOM is measurable.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(scrollWhenReady);
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [open, popoverYear, scrollWhenReady]);
  const todayKey = (() => {
    const t = isoWeekOf(new Date());
    return formatIsoWeekKey(t.year, t.week);
  })();
  const start = dateOfIsoWeek(parsed.year, parsed.week);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const dayLongFmt = new Intl.DateTimeFormat("en-IE", { weekday: "short", day: "2-digit", month: "short" });
  const triggerLabel = `Week ${parsed.week} · ${new Intl.DateTimeFormat("en-IE", { day: "2-digit", month: "short" }).format(start)} – ${new Intl.DateTimeFormat("en-IE", { day: "2-digit", month: "short" }).format(end)}`;
  const subLabel = `${dayLongFmt.format(start)} – ${dayLongFmt.format(end)}`;
  const weeks = useMemo(() => isoWeeksInYearList(popoverYear), [popoverYear]);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) setPopoverYear(parsed.year); }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-9 px-3 rounded-md border border-border bg-card hover:bg-secondary/40 text-left flex flex-col justify-center transition-colors"
          data-testid="jobs-week-picker-btn"
          title={subLabel}
        >
          <span className="text-[12px] font-extrabold leading-tight truncate">{triggerLabel}</span>
          <span className="text-[10px] text-muted-foreground leading-tight truncate">{subLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-2"
        align="start"
        data-testid="jobs-week-picker-popover"
      >
        <div className="flex items-center justify-between mb-2 px-1">
          <button
            type="button"
            onClick={() => setPopoverYear((y) => y - 1)}
            className="px-2 py-1 text-xs font-bold rounded hover:bg-secondary"
            data-testid="jobs-week-picker-prev-year"
          >‹ {popoverYear - 1}</button>
          <div className="text-sm font-extrabold tabular-nums mono">{popoverYear}</div>
          <button
            type="button"
            onClick={() => setPopoverYear((y) => y + 1)}
            className="px-2 py-1 text-xs font-bold rounded hover:bg-secondary"
            data-testid="jobs-week-picker-next-year"
          >{popoverYear + 1} ›</button>
        </div>
        <div className="max-h-[280px] overflow-y-auto space-y-0.5">
          {weeks.map((w) => {
            const isSelected = w.key === value;
            const isToday = w.key === todayKey;
            // Prefer scrolling to the actual selection when it belongs to
            // the year the popover is showing; otherwise fall back to
            // "today" so a year-nav click still lands near a useful row.
            const shouldRef = isSelected || (!weeks.some((x) => x.key === value) && isToday);
            return (
              <button
                key={w.key}
                ref={shouldRef ? selectedRowRef : undefined}
                type="button"
                onClick={() => { onChange(w.key); }}
                className={`w-full text-left px-2 py-1.5 rounded text-[12px] mono tabular-nums transition-colors ${
                  isSelected
                    ? "bg-accent text-accent-foreground font-bold ring-2 ring-accent/70"
                    : isToday
                      ? "bg-amber-50 text-amber-900 border border-amber-200"
                      : "hover:bg-secondary/60"
                }`}
                data-testid={`jobs-week-option-${w.key}`}
              >
                {w.label}{isToday && !isSelected ? " · today" : ""}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Phase G — Quick Mark-Paid popover. One Form-of-payment select + optional
// "Paid on" date. Uses the booking's existing payment_type (no second
// step). The fuller MarkPaidDialog below stays as the advanced flow for
// InvoiceDetail / BookingDetail.
function MarkPaidQuickPopover({ booking, paidMethods, paymentTypes = [], currency, onDone, testIdPrefix, variant = "row" }) {
  const [open, setOpen] = useState(false);
  const [paymentType, setPaymentType] = useState(booking.payment_type || "");
  const [paidMethodCode, setPaidMethodCode] = useState(booking.paid_method_code || "");
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPaymentType(booking.payment_type || paymentTypes[0]?.code || "");
    setPaidMethodCode(booking.paid_method_code || (paidMethods[0]?.code || ""));
    setPaidOn(new Date().toISOString().slice(0, 10));
  }, [open, booking.id, booking.payment_type, booking.paid_method_code, paidMethods, paymentTypes]);

  // Phase I — track the active payment type's flags so we can show the
  // off-the-books warning chip + drive the "invoice regenerated" hint.
  const selectedPt = paymentTypes.find((p) => p.code === paymentType);
  const isOffBooks = !!(selectedPt && (selectedPt.off_the_books || selectedPt.generates_invoice === false));
  const ptChanged = paymentType && paymentType !== booking.payment_type;

  const submit = async () => {
    if (!paidMethodCode || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        payment_type: paymentType || booking.payment_type,
        paid_method_code: paidMethodCode,
        paid_at: paidOn ? new Date(`${paidOn}T12:00:00`).toISOString() : undefined,
      };
      const { data } = await api.post(`/bookings/${booking.id}/mark-paid`, payload);
      toast.success(`${booking.booking_code} marked as paid`);
      onDone(data);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const triggerClass = variant === "row"
    ? "h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
    : "w-full bg-emerald-600 hover:bg-emerald-700 text-white";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size={variant === "row" ? "sm" : "default"}
          className={triggerClass}
          data-testid={`${testIdPrefix}-markpaid-btn`}
        >
          <CheckCircle2 className={variant === "row" ? "h-3 w-3 mr-1" : "h-3.5 w-3.5 mr-1"} /> Mark paid
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-3 space-y-3"
        align={variant === "row" ? "end" : "center"}
        data-testid={`${testIdPrefix}-markpaid-popover`}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-xs font-extrabold uppercase tracking-wider">{booking.booking_code}</div>
          <div className="text-[11px] text-muted-foreground">
            {booking.customer_name} · <span className="mono tabular-nums" title="Total incl. VAT">{formatCurrency(booking.total_incl_vat != null ? booking.total_incl_vat : (booking.subtotal || 0), currency)}</span>
          </div>
        </div>

        {/* Phase I — Payment Type at the top (editable). Switching here
            triggers Phase A's invoice regenerate on the backend. */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-bold">Payment type</Label>
          <Select value={paymentType} onValueChange={setPaymentType}>
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-markpaid-payment-type-select`}>
              <SelectValue placeholder="Choose payment type…" />
            </SelectTrigger>
            <SelectContent>
              {paymentTypes.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted-foreground italic">No payment types configured.</div>
              )}
              {paymentTypes.map((p) => (
                <SelectItem
                  key={p.code}
                  value={p.code}
                  data-testid={`${testIdPrefix}-markpaid-payment-type-${p.code}`}
                >
                  {p.name}{p.generates_invoice ? ` · ${(p.tax_rate || 0).toFixed(0)}% VAT` : " · Cash"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {ptChanged && (
            <div
              className={`mt-1 text-[10px] rounded px-2 py-1 ${isOffBooks ? "bg-amber-50 text-amber-900 border border-amber-200" : "bg-sky-50 text-sky-900 border border-sky-200"}`}
              data-testid={`${testIdPrefix}-markpaid-pt-warning`}
            >
              {isOffBooks
                ? "Switching to off-the-books — the existing invoice will be voided."
                : "Invoice will be regenerated with the new tax treatment."}
            </div>
          )}
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-bold">Form of payment</Label>
          <Select value={paidMethodCode} onValueChange={setPaidMethodCode}>
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-markpaid-method-select`}>
              <SelectValue placeholder="Choose method…" />
            </SelectTrigger>
            <SelectContent>
              {paidMethods.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted-foreground italic">No Paid Methods configured yet.</div>
              )}
              {paidMethods.map((p) => (
                <SelectItem
                  key={p.code}
                  value={p.code}
                  data-testid={`${testIdPrefix}-markpaid-method-${p.code}`}
                >
                  {p.name}{p.user_name ? ` · → ${p.user_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider font-bold">Paid on</Label>
          <Input
            type="date"
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
            className="h-9"
            data-testid={`${testIdPrefix}-markpaid-paidon`}
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-markpaid-cancel`}
          >Cancel</Button>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={!paidMethodCode || submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`${testIdPrefix}-markpaid-confirm`}
          >
            {submitting ? "Saving…" : "Confirm"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Phase 63.3 — Mark Paid dialog. Hits POST /api/bookings/{id}/mark-paid with
// the chosen payment_type + (for invoice-generating types) paid_method_code.
// Layout: radio cards for payment_type, then a conditional paid_method
// select. Submit button is disabled until the selection is valid.
function MarkPaidDialog({ booking, paymentTypes, paidMethods, currency, onClose, onDone }) {
  const initialPt = paymentTypes.find((p) => p.code === booking.payment_type)?.code
    || paymentTypes[0]?.code
    || "";
  const [paymentType, setPaymentType] = useState(initialPt);
  const [paidMethodCode, setPaidMethodCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedPt = paymentTypes.find((p) => p.code === paymentType);
  const needsPaidMethod = !!selectedPt?.generates_invoice;
  // Sensible default for paid_method — try to match the booking's current
  // method, fall back to the first non-staff method (so we don't accidentally
  // dump every cash payment onto the wrong staff member's ledger).
  useEffect(() => {
    if (!needsPaidMethod) { setPaidMethodCode(""); return; }
    if (paidMethodCode && paidMethods.find((p) => p.code === paidMethodCode)) return;
    const preferred = paidMethods.find((p) => p.code !== "cash_to_driver");
    setPaidMethodCode((preferred || paidMethods[0])?.code || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, needsPaidMethod, paidMethods.length]);

  const canSubmit = !!paymentType && (!needsPaidMethod || !!paidMethodCode) && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = { payment_type: paymentType };
      if (needsPaidMethod) payload.paid_method_code = paidMethodCode;
      const { data } = await api.post(`/bookings/${booking.id}/mark-paid`, payload);
      toast.success(`${booking.booking_code} marked as paid · ${selectedPt?.name || paymentType}`);
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-md" data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Mark <span className="mono">{booking.booking_code}</span> as paid
          </DialogTitle>
          <DialogDescription>
            <strong>{booking.customer_name}</strong>
            {" · "}
            <span className="mono tabular-nums" title="Total incl. VAT">{formatCurrency(booking.total_incl_vat != null ? booking.total_incl_vat : (booking.subtotal || 0), currency)}</span>
            <span className="block mt-1 text-[11px]">How was this paid?</span>
          </DialogDescription>
        </DialogHeader>

        {paymentTypes.length === 0 ? (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="mark-paid-no-types">
            No payment types configured. Add one in Settings → Payment Types first.
          </div>
        ) : (
          <div className="space-y-3" data-testid="mark-paid-pt-grid">
            <div className="grid grid-cols-2 gap-2">
              {paymentTypes.map((p) => {
                const selected = p.code === paymentType;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setPaymentType(p.code)}
                    className={`text-left px-3 py-2 rounded-[6px] border transition-colors ${
                      selected
                        ? "border-emerald-600 bg-emerald-50"
                        : "border-border bg-card hover:bg-secondary"
                    }`}
                    data-testid={`mark-paid-pt-${p.code}`}
                  >
                    <div className={`text-sm font-bold ${selected ? "text-emerald-900" : ""}`}>
                      {p.name}
                    </div>
                    <div className="text-[10px] mono uppercase tracking-wider text-muted-foreground mt-0.5">
                      {p.generates_invoice ? `Invoice · ${(p.tax_rate || 0).toFixed(0)}% VAT` : "Cash · no invoice"}
                    </div>
                  </button>
                );
              })}
            </div>
            {paymentType !== booking.payment_type && (
              <p
                className="text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded px-2 py-1"
                data-testid="invoice-markpaid-pt-warning"
              >
                Changing this regenerates the invoice with the new tax treatment.
              </p>
            )}

            {needsPaidMethod && (
              <div className="space-y-1" data-testid="mark-paid-method-block">
                <Label className="text-[11px] uppercase tracking-wider font-bold">Paid via</Label>
                <Select value={paidMethodCode} onValueChange={setPaidMethodCode}>
                  <SelectTrigger data-testid="mark-paid-method-select">
                    <SelectValue placeholder="Choose method…" />
                  </SelectTrigger>
                  <SelectContent>
                    {paidMethods.map((p) => (
                      <SelectItem key={p.code} value={p.code} data-testid={`mark-paid-method-${p.code}`}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Required for invoice-generating payment types. Drives where the cash lands in your reports + staff ledger.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="mark-paid-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="mark-paid-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <><CheckCircle2 className="h-4 w-4 mr-1" /> Mark as Paid</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

